// Solutions — the five focused consulting engagements (spec 24 Oct 2026).
// Names are PROVISIONAL pending legal/name screening: never add TM/® symbols.
// Copy is the approved public prose; internal diagnostics, weights, models and
// SOPs must NEVER be added here (IP safeguard, spec §15).

export const SOLUTIONS = [
  {
    id: "growth360",
    name: "Growth360",
    descriptor: "Business Growth Diagnostic & Opportunity Prioritization",
    seoTitle: "Business Growth Diagnostic Consulting — Growth360 | BLEZZINGS",
    seoDescription:
      "Identify why business growth has stalled, diagnose commercial constraints and prioritize profitable opportunities with BLEZZINGS Growth360.",
    h1: "Growth360 — Find the growth constraint that matters most.",
    subhead:
      "When sales stall or the next growth opportunity is unclear, more activity is not necessarily the answer. Growth360 identifies the commercial constraints worth solving and helps leadership prioritize where to act.",
    symptoms: [
      "Revenue growth has slowed, but teams disagree on why.",
      "Growth initiatives multiply without clear priorities.",
      "Sales, marketing, pricing and channels point to different problems.",
      "Leadership needs an evidence-led action plan rather than more assumptions.",
    ],
    perspective:
      "Growth is a connected commercial system. A weak channel, failing conversion stage or unprofitable customer mix can make an otherwise strong strategy look ineffective. The first task is to identify what is truly constraining performance.",
    stages: [
      "Align on the decision and baseline",
      "Review the major commercial growth drivers",
      "Investigate the most likely constraints",
      "Compare and size opportunities",
      "Build an accountable action roadmap",
    ],
    examine: [
      "Market/customer opportunity",
      "Revenue drivers",
      "Offer/portfolio",
      "Pricing and economics",
      "Sales and distribution",
      "Demand and digital performance",
    ],
    deliverables: [
      "Commercial growth diagnostic",
      "Constraint and opportunity map",
      "Prioritized growth initiatives",
      "Executive roadmap with decision owners and measures",
    ],
    audience: "Founders, CEOs, managing directors, P&L/business heads and growth leaders.",
    duration: "Approximately 3–5 weeks, subject to scope, data quality and stakeholder access.",
    image: "/photos/sub-growth-strategy.jpg",
    imageAlt: "Indian leadership team reviewing a long-term growth roadmap on a wall chart",
    examineIcons: ["Target", "TrendingUp", "Layers", "CircleDollarSign", "Network", "Gauge"],
    ctaHeading: "Let's find the constraint holding your growth back.",
    ctaLabel: "Discuss Your Growth Challenge",
    tools: [
      { name: "Growth Stall & Bottleneck Diagnostic", url: "/tools/growth-bottleneck-diagnostic" },
      { name: "Growth System Health Score", url: "/tools/growth-system-scorecard" },
    ],
    toolNote:
      "The free Growth System Health Score is an introductory self-assessment across nine dimensions. It does not replace a validated enterprise diagnostic.",
    insights: [
      { title: "You're Growing. You're Not Scaling. The Difference Is Your Margin.", url: "/insights/growth-not-scaling" },
    ],
    relatedCapability: { name: "Growth Strategy", url: "/capabilities/strategy-commercial-transformation/growth-strategy" },
    faqs: [
      {
        q: "What does a business growth diagnostic include?",
        a: "A structured review of your commercial growth drivers — market and customer opportunity, revenue drivers, portfolio, pricing, sales, distribution and demand — ending in a constraint map and a prioritized, owned action roadmap. The exact scope is agreed before work begins.",
      },
      {
        q: "How is Growth360 different from a free online assessment?",
        a: "Free assessments, including ours, are introductory self-scores. Growth360 is an evidence-led engagement: your actual commercial data is examined, hypotheses are tested, and recommendations carry sized opportunities and named decision owners.",
      },
      {
        q: "Do we need clean data before starting?",
        a: "No. Imperfect data is the norm, not the exception. Part of the diagnostic is establishing what the available evidence can and cannot support — and what to instrument next.",
      },
      {
        q: "Can BLEZZINGS support implementation after the diagnostic?",
        a: "Yes — activation support is available where commissioned, and is scoped separately from the diagnostic so you can decide with the findings in hand.",
      },
    ],
  },
  {
    id: "profitshield",
    name: "ProfitShield",
    descriptor: "Commercial Profit Leakage & Margin Recovery",
    seoTitle: "Profitability Improvement Consulting — ProfitShield | BLEZZINGS",
    seoDescription:
      "Find profit leakage across pricing, trade schemes, product mix and channels. Build a commercial margin improvement plan with BLEZZINGS.",
    h1: "ProfitShield — Find where your profits are leaking.",
    subhead:
      "Revenue growth means little when contribution and margins are shrinking. ProfitShield traces commercial margin leakage and helps prioritize realistic opportunities to recover value.",
    symptoms: [
      "Sales grow, but gross margin or contribution keeps falling.",
      "Discount schemes and trade investment are difficult to evaluate.",
      "Some customers, SKUs or channels look attractive on revenue but not profit.",
      "Teams cannot reconcile reported profitability with deal-level economics.",
    ],
    perspective:
      "Profit leakage rarely sits in one line of the P&L. It accumulates through realized prices, promotional decisions, mix, returns and cost-to-serve. Finding it starts with reconciling the economics, not automatically increasing prices.",
    stages: [
      "Establish the commercial baseline",
      "Trace price and margin erosion",
      "Locate customer/SKU/channel drivers",
      "Evaluate recovery levers and trade-offs",
      "Activate controls and measurement",
    ],
    examine: [
      "Price realization",
      "Discounts/trade spend",
      "SKU and category contribution",
      "Customer/account economics",
      "Channel cost-to-serve",
      "Commercial governance",
    ],
    deliverables: [
      "Profit leakage map",
      "Contribution and price-realization findings",
      "Prioritized recovery options",
      "Implementation and monitoring roadmap",
    ],
    audience: "CEOs, CFOs, business heads, commercial directors, category managers and P&L owners.",
    duration: "Approximately 3–6 weeks; implementation separately scoped.",
    image: "/photos/sub-pricing-revenue-management.jpg",
    imageAlt: "Indian commercial team analysing pricing and margin charts on a glass wall",
    examineIcons: ["Tag", "Percent", "ChartColumn", "Users", "Truck", "ShieldCheck"],
    ctaHeading: "Let's find where your profit is leaking.",
    ctaLabel: "Discuss Margin Improvement",
    tools: [
      { name: "Profit Leakage & Margin Pressure Analyzer", url: "/tools/profit-leakage-calculator" },
      { name: "Discount Damage Simulator", url: "/tools/discount-damage-calculator" },
    ],
    insights: [
      { title: "Your Prices Look Fine. Your Pocket Price Tells Another Story.", url: "/insights/how-to-identify-pricing-leakage" },
      { title: "Sales Grew 40%. Profit Fell.", url: "/insights/why-growth-not-translating-into-profit" },
    ],
    relatedCapability: { name: "Pricing & Revenue Management", url: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
    faqs: [
      {
        q: "Where does profit leakage usually occur?",
        a: "Typically across realized prices, discount and scheme decisions, product and customer mix, returns, and channel cost-to-serve — rarely in one visible line. The diagnostic reconciles deal-level economics with reported profitability to locate it.",
      },
      {
        q: "Will ProfitShield recommend price increases automatically?",
        a: "No. Price is one lever among several. Recovery options are evaluated on evidence and trade-offs — many recoveries come from discount discipline, mix and cost-to-serve, not list-price changes.",
      },
      {
        q: "Does this apply to B2B and consumer businesses?",
        a: "Yes. The mechanics differ — trade terms and key accounts in consumer distribution, bid and contract economics in B2B — but the leakage analysis applies to both.",
      },
      {
        q: "How are estimated recovery opportunities separated from realized results?",
        a: "Estimates are labelled as opportunities with stated assumptions, and the monitoring roadmap tracks what is actually realized after activation. The two are never blended.",
      },
    ],
  },
  {
    id: "salesmax",
    name: "SalesMax",
    descriptor: "Sales Productivity & Execution Excellence",
    seoTitle: "Sales Productivity Consulting & Sales Transformation — SalesMax | BLEZZINGS",
    seoDescription:
      "Improve sales productivity, territory coverage, pipeline conversion and execution with the BLEZZINGS SalesMax consulting approach.",
    h1: "SalesMax — Improve sales productivity by fixing the system.",
    subhead:
      "More salespeople and more reviews do not automatically create more sales. SalesMax helps build a more productive sales organization through better coverage, processes, capabilities, incentives and management discipline.",
    symptoms: [
      "Sales performance varies widely across territories or people.",
      "The team is busy, but selling time and conversion remain low.",
      "CRM activity and reporting do not lead to better decisions.",
      "Incentives, coaching and targets are misaligned with growth priorities.",
    ],
    perspective:
      "Sales performance reflects the system in which people operate. Effective transformation starts by understanding the commercial design, opportunities and friction before blaming individual effort.",
    stages: [
      "Segment markets and opportunities",
      "Examine productivity and conversion",
      "Find capacity/process/incentive constraints",
      "Redesign management and execution levers",
      "Pilot, coach and measure improvement",
    ],
    examine: [
      "Territory and account coverage",
      "Sales capacity and productivity",
      "Funnel/win rates",
      "Management cadence",
      "Roles and incentives",
      "Sales capabilities",
    ],
    deliverables: [
      "Sales productivity assessment",
      "Opportunity and conversion findings",
      "Sales operating improvements",
      "Prioritized execution/coaching plan",
    ],
    audience: "CEOs, sales directors, chief sales officers, business heads, regional sales leaders.",
    duration: "Approximately 4–6 weeks, conditional on scope and access.",
    image: "/photos/sub-sales-transformation.jpg",
    imageAlt: "Indian sales leadership team in a field performance review with territory boards",
    examineIcons: ["Map", "Gauge", "Funnel", "Repeat", "Award", "GraduationCap"],
    ctaHeading: "Let's build a sales system that performs.",
    ctaLabel: "Discuss Sales Performance",
    tools: [
      { name: "Sales Force Productivity Analyzer", url: "/tools/sales-productivity-calculator" },
      { name: "Territory Potential & Sales Coverage Optimizer", url: "/tools/territory-coverage-optimizer" },
    ],
    insights: [
      { title: "Your Sales Team Isn't Underperforming. Your System Is Failing Them.", url: "/insights/how-to-diagnose-sales-productivity" },
    ],
    relatedCapability: { name: "Sales Transformation", url: "/capabilities/strategy-commercial-transformation/sales-transformation" },
    faqs: [
      {
        q: "Is SalesMax sales training or sales transformation?",
        a: "Transformation. Training is one possible output, but the diagnostic looks at the system first — coverage, funnel conversion, management cadence, incentives and capability — because skills programmes layered onto a broken system rarely stick.",
      },
      {
        q: "Does it work for field sales and B2B teams?",
        a: "Yes. The engagement covers field forces, inside sales, key-account and B2B teams; the coverage and cadence analysis adapts to the motion you run.",
      },
      {
        q: "Can we improve sales results without adding headcount?",
        a: "That is usually the point. Productivity gains typically come from better coverage allocation, conversion and management routines long before headcount is the constraint.",
      },
      {
        q: "What implementation support is available?",
        a: "Where commissioned, we pilot the redesigned levers with your teams, coach managers on the new cadence and track the agreed measures — scoped separately from the diagnostic.",
      },
    ],
  },
  {
    id: "channelmax",
    name: "ChannelMax",
    descriptor: "Distribution & Channel Performance Optimization",
    seoTitle: "Distribution & Channel Strategy Consulting — ChannelMax | BLEZZINGS",
    seoDescription:
      "Optimize distributor economics, dealer productivity, market coverage and route-to-market performance with BLEZZINGS ChannelMax.",
    h1: "ChannelMax — Build distribution that performs, not just expands.",
    subhead:
      "More distributors and more outlets do not guarantee sustainable sales. ChannelMax examines partner economics, route-to-market choices and sell-through performance to build more productive distribution.",
    symptoms: [
      "Distribution width is increasing faster than sales per outlet.",
      "Distributors and dealers are not prioritizing the brand.",
      "Sell-in looks healthy but sell-through and stock rotation are weak.",
      "New territory expansion is expensive or creates channel conflict.",
    ],
    perspective:
      "Channel performance is an economic design challenge as much as a sales execution challenge. Partners prioritize products that work for their margins, inventory and effort; coverage without sustainable partner economics is fragile.",
    stages: [
      "Map market and channel access",
      "Diagnose partner economics and execution",
      "Compare channel options and risks",
      "Prioritize market/outlet opportunities",
      "Activate partner improvements and track sell-through",
    ],
    examine: [
      "Coverage and whitespace",
      "Distributor/dealer profitability",
      "Channel structure",
      "Inventory and secondary sales",
      "Cost-to-serve",
      "Channel conflict and governance",
    ],
    deliverables: [
      "Coverage and market-opportunity view",
      "Channel/partner economics assessment",
      "Route-to-market recommendations",
      "Phased distribution action plan",
    ],
    audience: "CEOs, commercial directors, national/regional sales heads, channel and distribution leaders.",
    duration: "Approximately 4–7 weeks, conditional on scope and data availability.",
    image: "/photos/sub-channel-route-to-market.jpg",
    imageAlt: "Indian distribution professionals reviewing a coverage map beside a stocked retail storefront",
    examineIcons: ["MapPin", "Handshake", "Network", "Boxes", "Truck", "Scale"],
    ctaHeading: "Let's build distribution that pays for itself.",
    ctaLabel: "Discuss Distribution Performance",
    tools: [
      { name: "Distributor ROI & Working Capital Simulator", url: "/tools/distributor-roi-calculator" },
      { name: "Route-to-Market Decision Engine", url: "/tools/route-to-market-selector" },
    ],
    insights: [
      { title: "Your Distributors Aren't Lazy. Your Economics Are.", url: "/insights/why-distributor-network-underperforming" },
      { title: "Channel Partners Push Whoever Pays Them Best", url: "/insights/why-channel-partners-dont-push-your-brand" },
    ],
    relatedCapability: { name: "Channel & Route-to-Market Strategy", url: "/capabilities/strategy-commercial-transformation/channel-route-to-market" },
    faqs: [
      {
        q: "Is ChannelMax meant only for FMCG?",
        a: "No. The partner-economics logic applies to consumer durables, building materials, pharma-adjacent distribution, B2B dealer networks and any business that reaches customers through intermediaries.",
      },
      {
        q: "Will it suggest replacing distributors?",
        a: "Not by default. Most engagements improve the economics and execution of existing partners first; structural changes are recommended only where the evidence supports them.",
      },
      {
        q: "How do you assess distributor profitability?",
        a: "Through the partner's own arithmetic — margins, operating costs, inventory turns and credit cycles — so recommendations are built on what makes the partner's business work, not just the brand's targets.",
      },
      {
        q: "Can it support direct, dealer, modern trade and B2B routes?",
        a: "Yes. The route-to-market comparison covers direct, distributor, dealer, modern trade and B2B structures, including hybrid models and their conflict risks.",
      },
    ],
  },
  {
    id: "commercemax",
    name: "CommerceMax",
    descriptor: "Profitable E-commerce & Marketplace Growth",
    seoTitle: "Profitable E-commerce & Marketplace Growth Consulting — CommerceMax | BLEZZINGS",
    seoDescription:
      "Improve e-commerce contribution margins, marketplace performance, acquisition efficiency and conversion with BLEZZINGS CommerceMax.",
    h1: "CommerceMax — Turn online growth into profitable growth.",
    subhead:
      "Revenue, orders and ROAS can improve while contribution per order declines. CommerceMax connects D2C and marketplace acquisition, conversion, pricing, fulfillment and retention to better commercial economics.",
    symptoms: [
      "Online sales rise while net contribution remains weak.",
      "Marketplace advertising spends more without sustainable payback.",
      "Returns, fees, shipping and discounts erode contribution.",
      "Traffic, conversion, repeat purchase and inventory decisions are disconnected.",
    ],
    perspective:
      "The objective is not to maximize ROAS in isolation. It is to improve the contribution economics of the online business while creating room for sustainable growth.",
    stages: [
      "Reconcile order and channel economics",
      "Diagnose acquisition and conversion",
      "Identify profitable SKU and channel opportunities",
      "Prioritize commercial tests and interventions",
      "Operate a measurement and improvement cadence",
    ],
    examine: [
      "SKU/order contribution",
      "Marketplace fees and returns",
      "Ad efficiency and acquisition",
      "PDP and conversion",
      "Pricing/merchandising",
      "Retention and repeat economics",
    ],
    deliverables: [
      "D2C/marketplace economics assessment",
      "Product/channel profitability priorities",
      "Acquisition and conversion improvement backlog",
      "Test-and-measure growth roadmap",
    ],
    audience: "E-commerce founders, CEOs, digital heads, marketplace managers, D2C and P&L leaders.",
    duration: "Approximately 3–6 weeks, conditional on scope and data availability.",
    image: "/photos/sub-ecommerce-marketplace-growth.jpg",
    imageAlt: "Indian e-commerce team managing marketplace listings with products and a laptop",
    examineIcons: ["ShoppingCart", "Undo2", "Megaphone", "MousePointerClick", "Tags", "HeartHandshake"],
    ctaHeading: "Let's make your online growth profitable.",
    ctaLabel: "Discuss Profitable E-commerce Growth",
    tools: [
      { name: "E-commerce Unit Economics & Contribution Calculator", url: "/tools/ecommerce-profitability-calculator" },
      { name: "Channel Economics Calculator", url: "/tools/channel-economics-calculator" },
    ],
    insights: [
      { title: "A 4x ROAS Can Lose You Money", url: "/insights/roas-is-not-profit" },
      { title: "Your Marketplace Revenue Is Growing. Your Profit Per Order Is Shrinking.", url: "/insights/why-marketplace-revenue-grows-but-profit-doesnt" },
    ],
    relatedCapability: { name: "E-commerce & Marketplace Growth", url: "/capabilities/digital-commerce/ecommerce-marketplace-growth" },
    faqs: [
      {
        q: "Is CommerceMax an audit or ongoing e-commerce management?",
        a: "It begins as a focused diagnostic with a prioritized backlog. Ongoing execution support is available where commissioned, but it is not a store-management or agency service.",
      },
      {
        q: "Does it cover Amazon, Flipkart, D2C and other marketplaces?",
        a: "Yes — the economics framework covers the major Indian marketplaces, quick commerce and your own D2C store, and compares channels on a like-for-like contribution basis.",
      },
      {
        q: "What is the difference between ROAS and contribution profit?",
        a: "ROAS divides attributed revenue by ad spend. Contribution subtracts product cost, fees, shipping, returns and discounts from that revenue first. A 4x ROAS can still lose money; contribution tells you whether growth is worth buying.",
      },
      {
        q: "Can BLEZZINGS help execute improvements after diagnosis?",
        a: "Yes, where commissioned — we help run the prioritized tests, set the measurement cadence and transfer the operating rhythm to your team.",
      },
    ],
  },
];

export const getSolution = (id) => SOLUTIONS.find((s) => s.id === id);

/* Which existing pages cross-link to which solution (spec §10).
 * Compact, single blocks — not repeated CTAs everywhere. */
export const TOOL_SOLUTION = {
  "growth-bottleneck-diagnostic": "growth360",
  "growth-system-scorecard": "growth360",
  "hidden-revenue-finder": "growth360",
  "profit-leakage-calculator": "profitshield",
  "discount-damage-calculator": "profitshield",
  "sales-productivity-calculator": "salesmax",
  "territory-coverage-optimizer": "salesmax",
  "distributor-roi-calculator": "channelmax",
  "route-to-market-selector": "channelmax",
  "ecommerce-profitability-calculator": "commercemax",
  "channel-economics-calculator": "commercemax",
};

export const INSIGHT_SOLUTION = {
  "growth-not-scaling": "growth360",
  "how-to-identify-pricing-leakage": "profitshield",
  "why-growth-not-translating-into-profit": "profitshield",
  "how-to-diagnose-sales-productivity": "salesmax",
  "why-distributor-network-underperforming": "channelmax",
  "why-channel-partners-dont-push-your-brand": "channelmax",
  "roas-is-not-profit": "commercemax",
  "why-marketplace-revenue-grows-but-profit-doesnt": "commercemax",
};

export const SERVICE_SOLUTION = {
  "strategy-commercial-transformation/growth-strategy": "growth360",
  "strategy-commercial-transformation/pricing-revenue-management": "profitshield",
  "strategy-commercial-transformation/sales-transformation": "salesmax",
  "strategy-commercial-transformation/channel-route-to-market": "channelmax",
  "digital-commerce/ecommerce-marketplace-growth": "commercemax",
};

/* Homepage Problems → solution deep links (spec §2). */
export const PROBLEM_SOLUTION = {
  "Growth Has Stalled": ["growth360"],
  "Profitability Is Under Pressure": ["profitshield"],
  "Sales & Distribution Underperform": ["salesmax", "channelmax"],
  "Digital Growth Isn't Profitable": ["commercemax"],
};
